import React, { useState } from 'react'
import {DollarSign, X, Heart} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'

interface TippingModalProps {
  isOpen: boolean
  onClose: () => void
  task: any
  agent: any
  onSuccess?: () => void
}

export function TippingModal({ isOpen, onClose, task, agent, onSuccess }: TippingModalProps) {
  const [tipAmount, setTipAmount] = useState(10)
  const [message, setMessage] = useState('')
  const [currency, setCurrency] = useState('USDC')
  const [loading, setLoading] = useState(false)

  const quickAmounts = [5, 10, 25, 50, 100]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Create tip transaction
      await lumi.entities.transactions.create({
        taskId: task._id,
        agentId: agent._id,
        requesterId: task.requesterId,
        amount: tipAmount,
        currency: currency,
        status: 'released',
        chain: 'ethereum',
        txHash: `0xtip${Date.now()}${Math.random().toString(36).substr(2, 9)}`,
        platformFee: 0,
        creator: task.requesterId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Update agent earnings
      const updatedEarnings = (agent.totalEarnings || 0) + tipAmount
      await lumi.entities.agents.update(agent._id, {
        totalEarnings: updatedEarnings,
        updatedAt: new Date().toISOString()
      })

      toast.success(`Sent $${tipAmount} tip to ${agent.name}!`)
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Tip failed:', error)
      toast.error('Failed to send tip')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full">
        <div className="border-b border-slate-700 p-6">
          <div className="flex justify-center mb-4">
            <img src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" alt="XyberMart" className="h-12" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Send a Tip</h2>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="text-center">
            <p className="text-slate-300 mb-2">Sending tip to</p>
            <p className="text-xl font-bold text-white">{agent?.name}</p>
            <p className="text-sm text-slate-400 mt-1">For exceptional work on: {task?.title}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-3">Quick Amounts</label>
            <div className="grid grid-cols-5 gap-2">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => setTipAmount(amount)}
                  className={`px-3 py-2 rounded-lg font-semibold transition-all ${
                    tipAmount === amount
                      ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Custom Amount</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="number"
                value={tipAmount}
                onChange={(e) => setTipAmount(parseFloat(e.target.value))}
                min="1"
                step="1"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="USDC">USDC</option>
              <option value="ETH">ETH</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Message (Optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Say something nice..."
              rows={3}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-700 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-pink-500/50 transition-all disabled:opacity-50"
            >
              {loading ? 'Sending...' : `Send $${tipAmount} Tip`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
